/**
 * Library — the on-device store of parsed flights.
 *
 * Nothing reaches our servers. This is the whole retention story for the
 * standalone viewer, so the copy in the UI has to match what happens here.
 *
 * Three object stores, deliberately split:
 *
 *   index     small records, always loaded, render the library list
 *   payloads  the heavy parsed contract, loaded only when a flight is opened
 *   analyses  AI narratives, keyed by hash + parser + prompt version
 *
 * If the list had to deserialise every payload to draw itself, opening the
 * page would get slower with every flight added. Splitting keeps list render
 * flat regardless of library size.
 *
 * Payloads are gzipped through CompressionStream, which typically gives 4 to 6
 * times on this kind of repetitive numeric JSON.
 */

const DB_NAME = 'dronedesk-logviewer';
const DB_VERSION = 2;

const STORE_INDEX = 'index';
const STORE_PAYLOADS = 'payloads';
const STORE_ANALYSES = 'analyses';
const STORE_LOGS = 'logs';

export class Library {
  constructor() {
    this._db = null;
    this.maxFlights = 50;
    this.maxPayloadBytes = 200 * 1024 * 1024;
  }

  async open() {
    if (this._db) return this._db;

    this._db = await new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains(STORE_INDEX)) {
          const store = db.createObjectStore(STORE_INDEX, { keyPath: 'hash' });
          store.createIndex('startEpoch', 'startEpoch');
        }
        if (!db.objectStoreNames.contains(STORE_PAYLOADS)) {
          db.createObjectStore(STORE_PAYLOADS, { keyPath: 'hash' });
        }
        if (!db.objectStoreNames.contains(STORE_ANALYSES)) {
          db.createObjectStore(STORE_ANALYSES, { keyPath: 'key' });
        }
        // v2: the gzipped raw log, kept so CSV/KML exports work on later visits
        // (the parser is stateless, so the original bytes must be re-sent).
        if (!db.objectStoreNames.contains(STORE_LOGS)) {
          db.createObjectStore(STORE_LOGS, { keyPath: 'hash' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Another tab is holding the library open.'));
    });

    return this._db;
  }

  /**
   * Ask the browser not to evict us.
   *
   * Without this, IndexedDB is cleared under storage pressure and a pilot's
   * library vanishes with no warning. Returns false if the browser declines,
   * which is a thing to surface rather than swallow.
   */
  async requestPersistence() {
    if (!navigator.storage || !navigator.storage.persist) return false;
    try {
      if (await navigator.storage.persisted()) return true;
      return await navigator.storage.persist();
    } catch {
      return false;
    }
  }

  async usage() {
    const records = await this.list();
    const totalBytes = records.reduce((sum, r) => sum + (r.payloadBytes || 0), 0);

    let quota = null;
    if (navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate();
        quota = estimate.quota || null;
      } catch {
        /* estimate is advisory */
      }
    }

    return { count: records.length, totalBytes, quota };
  }

  /** Index records only. Cheap enough to call on every render. */
  async list() {
    const db = await this.open();
    const records = await promisify(
      db.transaction(STORE_INDEX, 'readonly').objectStore(STORE_INDEX).getAll()
    );
    // Newest flight first. flownAt prefers the filename stamp, so a log whose
    // internal timestamp column was unreadable still sorts correctly.
    // startEpoch is the fallback for records written before flownAt existed.
    return records.sort(
      (a, b) => (b.flownAt || b.startEpoch || 0) - (a.flownAt || a.startEpoch || 0)
    );
  }

  async hasPayload(hash) {
    const db = await this.open();
    const key = await promisify(
      db.transaction(STORE_PAYLOADS, 'readonly').objectStore(STORE_PAYLOADS).getKey(hash)
    );
    return key !== undefined;
  }

  /** The full contract for one flight, decompressed. */
  async load(hash) {
    const db = await this.open();
    const record = await promisify(
      db.transaction(STORE_PAYLOADS, 'readonly').objectStore(STORE_PAYLOADS).get(hash)
    );

    if (!record) return null;

    const json = record.compressed
      ? await inflate(record.blob)
      : record.json;

    return JSON.parse(json);
  }

  /**
   * Store a parsed flight. Index and payload are written in one transaction so
   * the list can never show a row whose payload is missing.
   */
  async save(hash, contract, indexRecord) {
    const json = JSON.stringify(contract);
    const { blob, compressed } = await deflate(json);
    const payloadBytes = compressed ? blob.size : json.length;

    const db = await this.open();
    const tx = db.transaction([STORE_INDEX, STORE_PAYLOADS], 'readwrite');

    tx.objectStore(STORE_PAYLOADS).put(
      compressed ? { hash, blob, compressed: true } : { hash, json, compressed: false }
    );
    tx.objectStore(STORE_INDEX).put({ ...indexRecord, hash, payloadBytes });

    await transactionDone(tx);
    await this.prune();

    return payloadBytes;
  }

  /**
   * Store the raw log for a flight, gzipped, so CSV/KML exports work on later
   * visits. Separate from the payload so it can be dropped independently and is
   * never loaded just to render a flight.
   */
  async saveLog(hash, file) {
    let record;
    try {
      if (typeof CompressionStream === 'undefined') throw new Error('no CompressionStream');
      const gz = file.stream().pipeThrough(new CompressionStream('gzip'));
      record = { hash, blob: await new Response(gz).blob(), gzip: true };
    } catch (e) {
      // Fall back to storing the bytes uncompressed.
      record = { hash, blob: file instanceof Blob ? file : new Blob([file]), gzip: false };
    }

    const db = await this.open();
    const tx = db.transaction(STORE_LOGS, 'readwrite');
    tx.objectStore(STORE_LOGS).put(record);
    await transactionDone(tx);
  }

  /** The stored log for a flight: { blob, gzip } or null. */
  async loadLog(hash) {
    const db = await this.open();
    const record = await promisify(
      db.transaction(STORE_LOGS, 'readonly').objectStore(STORE_LOGS).get(hash)
    );
    return record || null;
  }

  async remove(hash) {
    const db = await this.open();
    const tx = db.transaction([STORE_INDEX, STORE_PAYLOADS, STORE_ANALYSES, STORE_LOGS], 'readwrite');

    tx.objectStore(STORE_INDEX).delete(hash);
    tx.objectStore(STORE_PAYLOADS).delete(hash);
    tx.objectStore(STORE_LOGS).delete(hash);

    // Analyses are keyed by hash plus versions, so sweep by prefix
    const analyses = tx.objectStore(STORE_ANALYSES);
    const cursorRequest = analyses.openCursor();
    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;
      if (typeof cursor.key === 'string' && cursor.key.startsWith(`${hash}:`)) cursor.delete();
      cursor.continue();
    };

    await transactionDone(tx);
  }

  async clear() {
    const db = await this.open();
    const tx = db.transaction([STORE_INDEX, STORE_PAYLOADS, STORE_ANALYSES, STORE_LOGS], 'readwrite');
    tx.objectStore(STORE_INDEX).clear();
    tx.objectStore(STORE_PAYLOADS).clear();
    tx.objectStore(STORE_ANALYSES).clear();
    tx.objectStore(STORE_LOGS).clear();
    await transactionDone(tx);
  }

  /**
   * Drop the oldest payloads past the limits, keeping their index records.
   *
   * The row stays in the list with a re-parse affordance rather than
   * disappearing, because a flight silently vanishing is alarming.
   */
  async prune() {
    const records = await this.list();
    if (records.length === 0) return [];

    let totalBytes = records.reduce((sum, r) => sum + (r.payloadBytes || 0), 0);
    const evict = [];

    for (let i = records.length - 1; i >= 0; i--) {
      const overCount = records.length - evict.length > this.maxFlights;
      const overBytes = totalBytes > this.maxPayloadBytes;
      if (!overCount && !overBytes) break;

      const record = records[i];
      if (record.payloadEvicted) continue;

      evict.push(record);
      totalBytes -= record.payloadBytes || 0;
    }

    if (evict.length === 0) return [];

    const db = await this.open();
    const tx = db.transaction([STORE_INDEX, STORE_PAYLOADS, STORE_LOGS], 'readwrite');

    for (const record of evict) {
      tx.objectStore(STORE_PAYLOADS).delete(record.hash);
      tx.objectStore(STORE_LOGS).delete(record.hash);
      tx.objectStore(STORE_INDEX).put({ ...record, payloadEvicted: true, payloadBytes: 0 });
    }

    await transactionDone(tx);
    return evict.map((r) => r.hash);
  }

  /* ----------------------------------------------------------- analyses */

  analysisKey(hash, parserVersion, promptVersion) {
    return `${hash}:${parserVersion}:${promptVersion}`;
  }

  async loadAnalysis(hash, parserVersion, promptVersion) {
    const db = await this.open();
    const record = await promisify(
      db
        .transaction(STORE_ANALYSES, 'readonly')
        .objectStore(STORE_ANALYSES)
        .get(this.analysisKey(hash, parserVersion, promptVersion))
    );
    // Older entries hold a bare string; newer ones hold { text, notice }.
    if (!record) return null;

    return record.analysis !== undefined ? record.analysis : record.text;
  }

  async saveAnalysis(hash, parserVersion, promptVersion, analysis) {
    const db = await this.open();
    const tx = db.transaction(STORE_ANALYSES, 'readwrite');
    tx.objectStore(STORE_ANALYSES).put({
      key: this.analysisKey(hash, parserVersion, promptVersion),
      analysis,
      createdAt: Date.now(),
    });
    await transactionDone(tx);
  }
}

/* -------------------------------------------------------------- hashing */

/**
 * SHA-256 of a file, matching what the server computes.
 *
 * Hashing client-side first means a re-upload of a file already in the library
 * costs nothing: no request, no quota, no bill.
 */
export async function hashFile(file) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/* ---------------------------------------------------------- compression */

async function deflate(json) {
  if (typeof CompressionStream === 'undefined') {
    return { blob: null, compressed: false };
  }

  try {
    const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
    const blob = await new Response(stream).blob();
    return { blob, compressed: true };
  } catch {
    return { blob: null, compressed: false };
  }
}

async function inflate(blob) {
  const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).text();
}

/* --------------------------------------------------------------- helpers */

function promisify(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted.'));
  });
}
